import { TreeManager } from './tree-manager';
import { Node } from './tree-view';

/**
 * Extends the standard Node with presentation-specific properties.
 */
export interface FlatNode<T> extends Node<T> {
  expanded: boolean;
  level: number;
}

/**
 * Orchestrates the UI state (expansion state) and prepares the tree data
 * for flat-list rendering based on node visibility.
 * * @template T - The type of the payload data.
 */
export class TreeViewModel<T> {
  private readonly expanded = new Set<string>();

  /**
   * @param treeManager - The underlying tree structure to project.
   */
  constructor(private readonly treeManager: TreeManager<T>) {}

  /** Marks a node's children as visible. */
  expandNode(key: string) {
    this.expanded.add(key);
  }

  /** Hides a node's children. */
  collapseNode(key: string) {
    this.expanded.delete(key);
  }

  /**
   * Flattens the tree into an array, evaluating visibility based on the
   * `expanded` state of ancestor nodes.
   * * @returns An array of FlatNode ready for rendering in a list component.
   */
  flattenNodes(): FlatNode<T>[] {
    return [...this.enumerateSubTrees(this.treeManager.roots)];
  }

  private *enumerateSubTrees(keys: readonly string[], level = 0): Generator<FlatNode<T>, void, unknown> {
    for (const key of keys) {
      const expanded = this.expanded.has(key);
      const node = this.treeManager.getNode(key);
      yield { ...node, expanded, level };
      if (expanded) {
        yield* this.enumerateSubTrees(node.children, level + 1);
      }
    }
  }
}
